/* eslint-disable max-lines */
import {
  IS_DEBUG_CHUNKS_ENABLED,
  IS_LIVE_TRANSCRIPTS_ENABLED,
} from '@/components/constants/flags';
import {
  addChunkToLiveTranscripts,
  pollLiveTranscripts,
} from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import { isSessionNotFoundError } from '@/lib/SessionRecordingProvider/errors';
import { stopMediaRecorder } from '@/lib/utils';
import { store } from '@/store';
import { type CreateLiveTranscriptionResponse } from '@/types';
import { wasRetriedByAuthHandler } from '@/utils/http/authRetry';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { sanitizeError } from '@heidi/runtime/logger';
import { type AxiosError } from 'axios';
import { isNetworkOrIdempotentRequestError } from 'axios-retry';
import { type IBlobEvent, type IMediaRecorder } from 'extendable-media-recorder';
import posthog from 'posthog-js';
import { DEFAULT_CHUNK_DURATION, GEMINI_TRANSCRIBER } from './offlineAudioRecorder';

// Moved to the session type contracts with `QuerySessionResponse`, whose
// `chunksUploading` field speaks it (OpenSpec `monorepo-session-data-core`
// C2). Re-exported so this module's importers keep their path; the recorder
// below is capture material and stays app-side.
import {
  trackChunkSessionMismatch,
  trackFailedToSendChunk,
  trackMediaRecorderFailedToStart,
  trackMediaStreamChunkTrackStateError,
} from '@/lib/analytics/scribe/general';
import {
  ChunkStatus,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
} from '@heidi/session/contracts';

export { ChunkStatus };

export const isUsingGeminiTranscriber = () => {
  const isGeminiTranscriber =
    posthog.getFeatureFlag('config-default-transcriber') === GEMINI_TRANSCRIBER;
  return isGeminiTranscriber;
};

export const getChunkDuration = () => {
  const isGeminiTranscriber = isUsingGeminiTranscriber();
  if (isGeminiTranscriber) {
    const posthogPayload = posthog.getFeatureFlag('config-gemini-transcriber-chunk-size');
    const duration = posthogPayload ? Number(posthogPayload) : DEFAULT_CHUNK_DURATION;
    return duration;
  }
  return DEFAULT_CHUNK_DURATION;
};

function getSecondsFromMilliseconds(num: number) {
  return Math.floor(num / 1000);
}

/**
 * Retry only when the server cannot have started a recording: a network
 * failure with no response. There is no body-sent gate like the upload-audio
 * condition's — this request carries no body to track. The residual risk is a
 * response lost after the server created the recording: the replay creates a
 * second recording id and orphans the first server-side, while the client
 * only ever uses an id from a response it received. That risk is accepted,
 * not papered over — retrying was the pre-existing design intent (retries: 3,
 * inert until the config-as-body fix in start()).
 *
 * Two exclusions mirror shouldRetryAudioUpload: configs already replayed by
 * the Tier-1 auth handler (replaying one makes the next 401 read as
 * session-dead and force-logs the user out), and pre-dispatch failures such
 * as confirmed desktop-offline, which never left the app — retrying those
 * only delays the failure by the backoff.
 */
const shouldRetryChunkRecordingStart = (sessionId: string, error: AxiosError) => {
  if (wasRetriedByAuthHandler(error)) {
    logger.warn('chunk_recording_start_retry_suppressed_after_auth_retry', {
      sessionId,
      httpStatus: error.response?.status,
      axiosErrorCode: error.code,
      requestMethod: error.config?.method,
    });
    return false;
  }
  // Request-interceptor failures such as confirmed desktop-offline never left the app.
  if (error.code === 'ERR_NETWORK' && error.request == null) {
    return false;
  }

  return isNetworkOrIdempotentRequestError(error);
};

class ChunkRecorder {
  private static instance: ChunkRecorder;
  private currentSessionId: string | null = null;
  private recordingId: string | null = null;

  private mediaRecorders: (IMediaRecorder | null)[];
  private dataAvailableHandlers: (((event: IBlobEvent) => void) | null)[];
  private timeouts: NodeJS.Timeout[] = [];

  private chunksStatus: ChunkStatus[] = [];
  private chunksCount: number = 0;
  private chunkDuration: number = DEFAULT_CHUNK_DURATION;

  private constructor() {
    this.mediaRecorders = [null, null];
    this.dataAvailableHandlers = [null, null];
  }

  public static getInstance(): ChunkRecorder {
    if (!ChunkRecorder.instance) {
      ChunkRecorder.instance = new ChunkRecorder();
    }
    return ChunkRecorder.instance;
  }

  public async start(sessionId: string) {
    if (this.currentSessionId && this.currentSessionId !== sessionId) {
      trackChunkSessionMismatch({
        currentSessionId: this.currentSessionId,
        newSessionId: sessionId,
      });

      this.clearAndStopAllMediaRecorders(); // Stop any ongoing recorders
      this.resetState(); // Reset the recorder state
    }
    this.currentSessionId = sessionId;

    this.chunkDuration = getChunkDuration();

    const time = new Date();

    const res: { data: CreateLiveTranscriptionResponse } = await scribeHttpV2.post(
      `/sessions/${sessionId}/restful-segment-transcription`,
      undefined,
      {
        timeout: 5 * 60 * 1000, // 5 minutes timeout
        'axios-retry': {
          retries: 3,
          retryCondition: (error: AxiosError) =>
            shouldRetryChunkRecordingStart(sessionId, error),
          onMaxRetryTimesExceeded: (error: AxiosError) => {
            // The throw replaces the AxiosError as the rejection, so capture
            // the root cause before it is lost to the caller's logging.
            logger.error('chunk_recording_start_retries_exhausted', {
              sessionId,
              httpStatus: error.response?.status,
              axiosErrorCode: error.code,
            });
            throw new Error('Failed to generate an id, please try again later');
          },
        },
      }
    );

    this.recordingId = res.data.recording_id;

    if (!this.recordingId) {
      throw new Error('Failed to recognize an id, please try again later');
    }

    logger.debug('time to upload', time.getTime() - new Date().getTime());
    this.chunksCount = 0;

    const firstChunkDuration = IS_LIVE_TRANSCRIPTS_ENABLED() ? 10 : this.chunkDuration;
    this.startRecorder(
      0,
      sessionId,
      res.data.recording_id,
      firstChunkDuration,
      this.chunkDuration
    );
    this.timeouts.push(
      setTimeout(
        () => {
          this.startRecorder(
            1,
            sessionId,
            res.data.recording_id,
            this.chunkDuration,
            this.chunkDuration
          );
        },
        (firstChunkDuration - 1) * 1000
      )
    );
  }

  private resetState() {
    this.currentSessionId = null;
    this.recordingId = null;
    this.chunksStatus = [];
    this.chunksCount = 0;
    this.mediaRecorders = [null, null];
    this.dataAvailableHandlers = [null, null];
  }

  private startRecorder(
    index: number,
    sessionId: string,
    recordingId: string,
    chunkDuration = DEFAULT_CHUNK_DURATION,
    nextChunkDuration = DEFAULT_CHUNK_DURATION
  ) {
    // Remove old listener before nulling to avoid async dataavailable firing after stop
    const oldRecorder = this.mediaRecorders.at(index);
    const oldHandler = this.dataAvailableHandlers.at(index);
    if (oldRecorder && oldHandler) {
      oldRecorder.removeEventListener('dataavailable', oldHandler);
    }
    this.mediaRecorders[index] = null;
    this.dataAvailableHandlers[index] = null;
    const stream = store.getState().audio.mediaStreamAudioDestinationNode?.stream;

    if (!stream) {
      return;
    }

    if (!this.recordingId) {
      return;
    }

    const track = stream.getAudioTracks().at(0); // Get the first audio track
    if (track) {
      if (track.muted || !track.enabled || track.readyState === 'ended') {
        trackMediaStreamChunkTrackStateError({
          error: `${track.id} track has error, track.enabled: ${track.enabled}, track.muted: ${track.muted}, track.readyState: ${track.readyState}`,
        });
      }
    } else {
      trackMediaStreamChunkTrackStateError({
        error: `No track found`,
      });
    }
    const chunkStartTime = Date.now();

    this.mediaRecorders[index] = new MediaRecorder(stream, {});
    const handler = (event: IBlobEvent) => {
      const chunkEndTime = Date.now();
      const duration = chunkEndTime - chunkStartTime;
      void this.uploadChunk(
        event,
        sessionId,
        recordingId,
        IS_LIVE_TRANSCRIPTS_ENABLED()
          ? getSecondsFromMilliseconds(duration)
          : this.chunkDuration
      );
    };
    this.dataAvailableHandlers[index] = handler;
    this.mediaRecorders.at(index)?.addEventListener('dataavailable', handler);

    // intentionally longer than timeout so it does not send ondataavailable
    const timesliceDuration = chunkDuration * 1000 * 2;
    try {
      this.mediaRecorders.at(index)?.start(timesliceDuration);
    } catch (e) {
      if (!this.currentSessionId) {
        return;
      }
      trackMediaRecorderFailedToStart({
        sessionId: this.currentSessionId,
        recordingId: this.recordingId,
        chunkCount: this.chunksCount,
        error: String(e),
      });
      logger.error('RECORDER ERROR', index, e);
    }

    this.timeouts.push(
      setTimeout(() => {
        const recorder = this.mediaRecorders.at(index);
        if (recorder) {
          stopMediaRecorder(recorder);
        }
        this.timeouts.push(
          setTimeout(() => {
            this.startRecorder(
              index,
              sessionId,
              recordingId,
              nextChunkDuration,
              nextChunkDuration
            );
          }, nextChunkDuration * 1000)
        );
      }, chunkDuration * 1000)
    );
  }

  private clearAndStopAllMediaRecorders() {
    this.timeouts.forEach((t) => clearTimeout(t));
    this.mediaRecorders.forEach((m, index) => {
      if (m) {
        const handler = this.dataAvailableHandlers.at(index);
        if (handler) {
          // Remove before stop so the final dataavailable event from stop() is not processed
          m.removeEventListener('dataavailable', handler);
          this.dataAvailableHandlers[index] = null;
        }
        logger.debug('stopping recorder', index);
        stopMediaRecorder(m);
      }
    });
  }

  public stop(sessionId: string) {
    // stop all timeouts and mediarecorders
    const recordingIdDuringStop = this.recordingId;
    this.clearAndStopAllMediaRecorders();
    // reset chunksUploadingFinished to false
    reactQueryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId: sessionId }],
      (oldData?: QuerySessionResponse) => {
        if (!oldData) {
          return undefined;
        }
        return { ...oldData, chunksUploadingFinished: false };
      }
    );

    const interval = setInterval(() => {
      const pending = this.chunksStatus.find((x) => x === ChunkStatus.QUEUED);
      if (!pending) {
        clearInterval(interval);
        if (!recordingIdDuringStop) {
          this.updateChunkToFinish(sessionId);
          return;
        }
        void this.finish(sessionId, recordingIdDuringStop);
      }
    }, 1000);
  }

  private updateChunkToFinish(sessionId: string) {
    reactQueryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId: sessionId }],
      (oldData?: QuerySessionResponse) => {
        if (!oldData) {
          return undefined;
        }

        const newData: QuerySessionResponse = {
          ...oldData,
          chunksUploadingFinished: true,
        };
        return newData;
      }
    );
  }

  public async finish(sessionId: string, recordingId: string) {
    const time = new Date();

    try {
      const { data } = await scribeHttpV2.post<{
        is_success: boolean;
        recording_id: string;
      }>(
        `/sessions/${sessionId}/restful-segment-transcription/${recordingId}:finish`,
        {}
      );
      if (data.is_success) {
        logger.debug('time to upload', time.getTime() - new Date().getTime());
      } else {
        logger.error('api return not is success');
      }
    } catch (err) {
      logger.error({
        event_type: 'CHUNK_RECORDING_FINISH_API_FAILED',
        details: {
          sessionId,
          recordingId,
          errorName: err instanceof Error ? err.name : typeof err,
          ...sanitizeError(err),
        },
      });
    } finally {
      this.updateChunkToFinish(sessionId);
      this.clearAndStopAllMediaRecorders();
      this.resetState();
    }
  }

  // if 1 or more chunks are missing or all chunks missing, we need to reupload the whole recording
  public chunkHasIssues() {
    const chunksIssues = this.chunksStatus.filter(
      (chunk) => chunk === ChunkStatus.FAILED
    ).length;
    const allChunks = this.chunksStatus.length;
    // make sure its not all chunks thats missing, if 1 or more chunks
    return allChunks !== 0 && (allChunks === chunksIssues || chunksIssues > 0);
  }

  public allChunksUploaded() {
    if (this.chunksCount === 0) {
      return true;
    }
    return this.chunksStatus.every((chunk) => chunk === ChunkStatus.COMPLETED);
  }

  public async uploadChunk(
    event: IBlobEvent,
    sessionId: string,
    recordingId: string,
    duration: number
  ) {
    const chunk = event.data;
    if (!recordingId) {
      return;
    }

    const id = this.chunksCount;
    this.chunksCount++;

    if (chunk.size < 1042) {
      logger.debug('chunk too small, marking as not sending', chunk.size);
      this.chunksStatus[id] = ChunkStatus.COMPLETED;
      return;
    }

    this.chunksStatus[id] = ChunkStatus.QUEUED;

    const formData = new FormData();
    formData.append('file', chunk, 'chunk-' + this.chunksCount + '.mp4');
    formData.append('index', String(id));
    formData.append('duration', String(duration));

    // useful to download chunks
    if (getEnvironment() !== ENV_NAME.PROD && IS_DEBUG_CHUNKS_ENABLED()) {
      const url = window.URL.createObjectURL(chunk);
      logger.debug(url, id);
      // window.open(url);
    }

    // const sessionId = store.getState().session.sessionId;

    try {
      const time = new Date();
      addChunkToLiveTranscripts({
        chunkId: id,
        sessionId,
        duration: duration,
      });

      const { data } = await scribeHttpV2.post<{ is_success: boolean }>(
        `/sessions/${sessionId}/restful-segment-transcription/${recordingId}:transcribe`,
        formData,
        {
          timeout: 5 * 60 * 1000, // 5 minutes timeout
          'axios-retry': {
            retries: 3,
            retryCondition: (error: AxiosError) => {
              // Same exclusion as the recording-start condition above:
              // replaying an auth-retried config makes the next 401 read as
              // session-dead and force-logs the user out.
              if (wasRetriedByAuthHandler(error)) {
                logger.warn('chunk_upload_retry_suppressed_after_auth_retry', {
                  sessionId,
                  recordingId,
                  chunkId: id,
                  httpStatus: error.response?.status,
                  axiosErrorCode: error.code,
                });
                return false;
              }

              if (isSessionNotFoundError(error)) {
                return false;
              }

              return (
                isNetworkOrIdempotentRequestError(error) || error.response?.status === 408
              );
            },
            validateResponse: (response: { data: { is_success: boolean } }) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return response.data.is_success;
            },
            onMaxRetryTimesExceeded: () => {
              this.chunksStatus[id] = ChunkStatus.FAILED;
              trackFailedToSendChunk({
                sessionId: sessionId,
                recordingId: recordingId,
                chunkId: id,
              });
            },
          },
        }
      );

      if (data.is_success) {
        this.chunksStatus[id] = ChunkStatus.COMPLETED;
        logger.debug('time to upload', time.getTime() - new Date().getTime());

        void pollLiveTranscripts(sessionId);
      } else {
        this.chunksStatus[id] = ChunkStatus.FAILED;
      }
    } catch {
      this.chunksStatus[id] = ChunkStatus.FAILED;
    }

    reactQueryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId: sessionId }],
      (oldData?: QuerySessionResponse) => {
        if (!oldData) {
          return undefined;
        }

        const newData: QuerySessionResponse = {
          ...oldData,
          chunksUploading: [...this.chunksStatus],
        };
        return newData;
      }
    );
  }
}

export default ChunkRecorder.getInstance();
